import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { apiFetch, ApiError, configureAuth, finishAuthBootstrap, startAuthBootstrap } from "../api/client";
import { clearIdentity, identify } from "../analytics/tracker";
import { AuthContext } from "./useAuth";

interface User {
  id: string;
  email: string;
  role: string;
}

/** Consensi facoltativi della registrazione: E) marketing diretto, F) profilazione. */
export interface SignupConsents {
  marketing: boolean;
  profiling: boolean;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  status: "loading" | "ready";
  // signup non logga più: crea l'account e invia l'email di verifica.
  signup: (email: string, password: string, consents: SignupConsents, locale?: string) => Promise<{ status: string; email: string }>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: (email: string, locale?: string) => Promise<void>;
}


/**
 * Un solo refresh in volo alla volta, per tutta la pagina.
 *
 * Il refresh RUOTA il cookie: due refresh concorrenti (StrictMode che monta due
 * volte, un 401 mentre il bootstrap è in corso) usavano lo stesso cookie, il
 * secondo veniva rifiutato e la sessione si perdeva — tipicamente al ritorno da
 * Stripe (R-29, R-64). Ora chi arriva mentre un refresh è in corso riceve lo
 * stesso risultato invece di farne un altro.
 */
let refreshInFlight: Promise<string | null> | null = null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready">("loading");
  const tokenRef = useRef<string | null>(null);
  // «Sessione pronta»: aperta qui, prima degli effetti dei figli (le pagine che
  // chiamano le API al montaggio la aspettano); chiusa a bootstrap finito.
  startAuthBootstrap();

  // Keep ref in sync for the API client
  useEffect(() => {
    tokenRef.current = accessToken;
  }, [accessToken]);

  const refreshSession = useCallback(async (): Promise<string | null> => {
    if (!refreshInFlight) {
      refreshInFlight = (async () => {
        try {
          const data = await apiFetch<{ access_token: string } | undefined>(
            "/api/v1/auth/refresh",
            { method: "POST", skipAuthWait: true },
            true, // skip retry on this call itself
          );
          const token = data?.access_token ?? null;
          tokenRef.current = token;
          return token;
        } catch {
          tokenRef.current = null;
          return null;
        } finally {
          refreshInFlight = null;
        }
      })();
    }
    const token = await refreshInFlight;
    setAccessToken(token);
    if (!token) setUser(null);
    return token;
  }, []);

  // Wire up the API client's auth hooks (sincrono: prima di qualsiasi effetto
  // dei figli, che potrebbero già chiamare le API).
  configureAuth(() => tokenRef.current, refreshSession);

  // On mount: try silent refresh then fetch /me
  useEffect(() => {
    let active = true;
    (async () => {
      const token = await refreshSession();
      if (token) {
        try {
          const me = await apiFetch<User>("/api/v1/me", { skipAuthWait: true });
          if (active) setUser(me);
          // Anche al ripristino della sessione: chi torna già autenticato non
          // passa dal login, e senza questo la sua navigazione resterebbe
          // anonima per tutta la visita.
          identify(me.id, token, "login");
        } catch {
          if (active) setUser(null);
        }
      }
      if (active) setStatus("ready");
      finishAuthBootstrap();
    })();
    return () => { active = false; };
  }, [refreshSession]);

  const signup = useCallback(async (email: string, password: string, consents: SignupConsents, locale = "it") => {
    // Nessuna sessione: l'account resta non verificato finché l'utente non
    // conferma l'email cliccando il link ricevuto.
    return await apiFetch<{ status: string; email: string }>(
      "/api/v1/auth/signup",
      {
        method: "POST",
        body: JSON.stringify({
          email,
          password,
          locale,
          marketing_consent: consents.marketing,
          profiling_consent: consents.profiling,
        }),
      },
      true,
    );
  }, []);

  const verifyEmail = useCallback(async (token: string) => {
    await apiFetch<{ status: string }>(
      "/api/v1/auth/verify-email",
      { method: "POST", body: JSON.stringify({ token }) },
      true,
    );
  }, []);

  const resendVerification = useCallback(async (email: string, locale = "it") => {
    await apiFetch<{ status: string }>(
      "/api/v1/auth/resend-verification",
      { method: "POST", body: JSON.stringify({ email, locale }) },
      true,
    );
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiFetch<{ access_token: string }>(
      "/api/v1/auth/login",
      { method: "POST", body: JSON.stringify({ email, password }) },
      true,
    );
    setAccessToken(data.access_token);
    tokenRef.current = data.access_token;
    const me = await apiFetch<User>("/api/v1/me");
    setUser(me);
    // Collega la navigazione anonima appena conclusa a questa persona: la
    // parte di visita precedente all'accesso è quasi sempre quella in cui si
    // decide l'acquisto, e senza questo resterebbe scollegata da chi compra.
    // Non attende e non può far fallire l'accesso.
    identify(me.id, data.access_token, "login");
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch("/api/v1/auth/logout", { method: "POST" }, true);
    } catch {
      // ignore
    }
    setAccessToken(null);
    setUser(null);
    tokenRef.current = null;
    // Senza, un secondo accesso dalla stessa scheda con un altro utente non
    // verrebbe collegato: il marcatore riterrebbe il lavoro già fatto.
    clearIdentity();
    window.dispatchEvent(new Event("gb:logout"));
  }, []);

  const value = useMemo(
    () => ({ user, accessToken, status, signup, login, logout, verifyEmail, resendVerification }),
    [user, accessToken, status, signup, login, logout, verifyEmail, resendVerification],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { ApiError };
